<template>
  <!-- When visible, render the real image -->
  <NuxtPicture
    v-if="shouldLoad"
    v-bind="pictureProps"
    class="ptc-picture"
  />

  <!-- Placeholder observed by IntersectionObserver -->
  <div
    v-else
    ref="observerRef"
    class="ptc-picture-placeholder"
  />
</template>

<script setup lang="ts">
import {
  useIntersectionObserver,
  useWindowScroll,
  useWindowSize,
  watchThrottled,
} from '@vueuse/core';
import { ref, computed } from 'vue';
import type { PropType } from 'vue';

const props = defineProps({
  provider: {
    type: String as PropType<'sanity'>,
    required: true,
  },

  src: {
    type: String,
    required: true,
  },

  alt: {
    type: String,
    default: '',
  },

  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    default: 'lazy',
  },

  imgAttrs: {
    type: Object,
    default: () => ({}),
  },

  modifiers: {
    type: Object,
    default: () => ({}),
  },

  width: {
    type: [Number, String],
    default: undefined,
  },

  height: {
    type: [Number, String],
    default: undefined,
  },
  format: {
    type: String as PropType<'webp' | 'jpg' | 'png'>,
    default: 'webp',
  },
});

/**
 * Controls if the image should render
 */
const shouldLoad = ref(props.loading === 'eager');

/**
 * Placeholder element reference
 */
const observerRef = useTemplateRef<HTMLElement>('observerRef');

// handle lazy loading with intersection observer and scroll position as a backup
const handleLazy = () => {
  if (import.meta.server) return;
  const { y } = useWindowScroll();
  const { height: windowHeight } = useWindowSize();

  let stopScrollWatch = () => {};

  const loadPicture = () => {
    if (shouldLoad.value) return;

    console.log('Loading picture:', props.src);

    shouldLoad.value = true;
    stopIntersectionObserver();
    stopScrollWatch();
  };

  const { stop: stopIntersectionObserver } = useIntersectionObserver(
    observerRef,
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      loadPicture();
    },
    {
      rootMargin: '250px',
      threshold: 0.01,
    }
  );

  stopScrollWatch = watchThrottled(
    [y, windowHeight],
    ([scrollY, viewportHeight]) => {
      if (scrollY >= viewportHeight * 1.5) {
        loadPicture();
      }
    },
    {
      immediate: true,
      throttle: 100,
    }
  );
};
onMounted(() => {
  if (props.loading === 'lazy') {
    handleLazy();
  }
});

const pictureProps = computed(() => ({
  provider: props.provider,
  src: props.src,
  alt: props.alt,
  imgAttrs: props.imgAttrs,
  modifiers: props.modifiers,
  format: props.format,
  sizes: '100vw sm:50vw lg:800px',
  ...(props.width && { width: props.width }),
  ...(props.height && { height: props.height }),
}));
</script>

<style scoped>
.ptc-picture-placeholder {
  display: block;
  width: 100%;
  height: 100%;
}

/* Important: placeholder must have height */
.ptc-picture-placeholder {
  aspect-ratio: var(--aspect-ratio, 16 / 9);
  background: #f3f3f3;
}
</style>
